import { CellValueChangedEvent, GetRowIdFunc } from "@ag-grid-community/core";
import { AgGridReactProps } from "@ag-grid-community/react";
import { cloneDeep } from "lodash-es";
import { useMemo } from "react";
import { useArray, UseArraySetter } from "shared/hooks/useArrayState";

export type MaybernTableRowWithId<T> = T & {
  id: string;
};

export interface UseMaybernTableRowControllerReturn<T> {
  addRow: (row: MaybernTableRowWithId<T>) => void;
  removeRow: (rowIndex: number) => void;
  propsForRows: AgGridReactProps;
}

export const useMaybernTableRowController = <T>(
  rows: MaybernTableRowWithId<T>[],
  setRows: UseArraySetter<MaybernTableRowWithId<T>>,
): UseMaybernTableRowControllerReturn<T> => {
  const {
    add: addRow,
    removeAt: removeRow,
    updateIf: updateRowIf,
  } = useArray(rows, setRows);

  // cloneDeep because AG Grid likes to mutate rowData,
  // breaking dependency detection in hooks.
  // this is a separate useMemo() so rows are not re-created when other props change
  const rowData = useMemo(() => cloneDeep(rows), [rows]);

  return useMemo(
    () => ({
      addRow,
      removeRow,
      propsForRows: {
        getRowId,
        rowData,
        animateRows: false,
        readOnlyEdit: true,
        onCellValueChanged: (
          event: CellValueChangedEvent<MaybernTableRowWithId<T>>,
        ) => {
          const row = event.node.data!;
          updateRowIf((oldRow) => (oldRow.id === row.id ? row : null));
        },
      },
    }),
    [addRow, removeRow, rowData, updateRowIf],
  );
};

const getRowId: GetRowIdFunc<MaybernTableRowWithId<unknown>> = (params) =>
  params.data.id;
